import {
	ArrowDownUpIcon,
	CheckIcon,
	ChevronRightIcon,
	LayersIcon,
	MoreVerticalIcon,
	PencilIcon,
	PlusIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CreatePricingCampaign } from '@/features/pricing-campaign/create'

import CheckWaveIcon from '@/shared/components/icons/CheckWaveIcon'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSkeleton,
	SidebarMenuSub,
} from '@/shared/components/ui/sidebar'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { ChangePricingCampaignsPriority } from './global-pc-set/ChangePricingCampaignsPriority'
import { DeleteGlobalPcSet } from './global-pc-set/DeleteGlobalPcSet'
import { DublicateGlobalPcSet } from './global-pc-set/DublicateGlobalPcSet'
import { PricingCampaignItem } from './global-pc-set/pricing-campaign/PricingCampaignItem'
import { RenameGlobalPcSet } from './global-pc-set/RenameGlobalPcSet'
import { SetGlobalPcSetAsMain } from './global-pc-set/SetGlobalPcSetAsMain'
import { useGlobalPcSets } from './GlobalPcSetsProvider'
import { GlobalPcSetsToolbar } from './GlobalPcSetsToolbar'

export const GlobalPcSetsSidebar = () => {
	const { t } = useTranslation()

	const {
		globalPcSets,
		isLoading,
		isGroupExpanded,
		toggleGroup,
		selectedGlobalSetIds,
		toggleGlobalSetSelection,
	} = useGlobalPcSets()

	return (
		<Sidebar collapsible='none' className='w-full shrink-0 border-r'>
			<GlobalPcSetsToolbar />
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{isLoading ? (
								<LoaderPlaceholder />
							) : globalPcSets?.length > 0 ? (
								globalPcSets.map((globalPcSet) => {
									const isMainSet = globalPcSet.is_main
									const globalPcSetId = globalPcSet.id

									return (
										<SidebarMenuItem key={globalPcSetId}>
											<Collapsible
												open={isGroupExpanded(globalPcSetId)}
												onOpenChange={() => toggleGroup(globalPcSetId)}
												className='group/collapsible [&[data-state=open]_[data-slot=icon]]:rotate-90'
											>
												<div className='sticky top-0 z-10 w-full items-center bg-sidebar'>
													<div className='flex w-full items-center gap-2'>
														<CollapsibleTrigger asChild>
															<SidebarMenuButton
																size='sm'
																className='flex w-full items-center'
															>
																<Checkbox
																	data-testid='global-pc-set-checkbox'
																	checked={selectedGlobalSetIds.includes(
																		globalPcSetId
																	)}
																	onClick={(e) => e.stopPropagation()}
																	onChange={() =>
																		toggleGlobalSetSelection(globalPcSetId)
																	}
																/>
																<ChevronRightIcon
																	className='transition-transform'
																	data-slot='icon'
																/>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<span>{globalPcSet.name}</span>
																	</TooltipTrigger>
																	<TooltipContent>
																		{globalPcSet.name}
																	</TooltipContent>
																</Tooltip>
																{isMainSet && (
																	<Tooltip>
																		<TooltipTrigger asChild>
																			<CheckWaveIcon />
																		</TooltipTrigger>
																		<TooltipContent>
																			{t('app.live')}
																		</TooltipContent>
																	</Tooltip>
																)}
															</SidebarMenuButton>
														</CollapsibleTrigger>
													</div>
													{!isMainSet && (
														<CreatePricingCampaign
															location='global-sets-sidebar'
															scope={{
																globalPcSetId,
																disableOptimizationGroups: true,
															}}
														>
															<SidebarMenuAction
																showOnHover
																className='right-7 cursor-pointer'
																disabled={isMainSet}
															>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<PlusIcon />
																	</TooltipTrigger>
																	<TooltipContent>
																		{t('global_set.add_pc', {
																			defaultValue: 'Add PC',
																		})}
																	</TooltipContent>
																</Tooltip>
															</SidebarMenuAction>
														</CreatePricingCampaign>
													)}

													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<SidebarMenuAction
																className='cursor-pointer'
																showOnHover
															>
																<MoreVerticalIcon />
															</SidebarMenuAction>
														</DropdownMenuTrigger>
														<DropdownMenuContent side='bottom' align='end'>
															<RenameGlobalPcSet globalPcSet={globalPcSet}>
																<DropdownMenuItem disabled={isMainSet}>
																	<PencilIcon />
																	{t('app.rename', { defaultValue: 'Rename' })}
																</DropdownMenuItem>
															</RenameGlobalPcSet>
															<DublicateGlobalPcSet id={globalPcSetId}>
																<DropdownMenuItem>
																	<LayersIcon />
																	{t('general_duplicate')}
																</DropdownMenuItem>
															</DublicateGlobalPcSet>
															<ChangePricingCampaignsPriority
																globalPcSetId={globalPcSetId}
															>
																<DropdownMenuItem disabled={isMainSet}>
																	<ArrowDownUpIcon />
																	{t('pc.action.change_priority', {
																		defaultValue: 'Change priority',
																	})}
																</DropdownMenuItem>
															</ChangePricingCampaignsPriority>
															<SetGlobalPcSetAsMain
																id={globalPcSetId}
																name={globalPcSet.name}
															>
																<DropdownMenuItem disabled={isMainSet}>
																	<CheckIcon />
																	{t('global_set.set_as_main', {
																		defaultValue: 'Apply as Main Set',
																	})}
																</DropdownMenuItem>
															</SetGlobalPcSetAsMain>
															<DeleteGlobalPcSet id={globalPcSetId}>
																<DropdownMenuItem
																	variant='destructive'
																	disabled={isMainSet}
																>
																	<Trash2Icon />
																	{t('general_delete')}
																</DropdownMenuItem>
															</DeleteGlobalPcSet>
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
												<CollapsibleContent className='mb-2 space-y-2'>
													<SidebarMenuSub>
														{globalPcSet?.pricingCampaigns?.map(
															(pricingCampaign) => (
																<PricingCampaignItem
																	key={pricingCampaign.id}
																	pricingCampaign={pricingCampaign}
																/>
															)
														)}
													</SidebarMenuSub>
												</CollapsibleContent>
											</Collapsible>
										</SidebarMenuItem>
									)
								})
							) : (
								<span className='text-center text-sm'>
									{t('no_global_sets', {
										defaultValue: 'No global sets found',
									})}
								</span>
							)}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
		</Sidebar>
	)
}

const LoaderPlaceholder = () => (
	<>
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
	</>
)
